import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  useGetAdminFeedbacksQuery,
  useDeleteFeedbackMutation,
} from "@/store/api/homepageApi";
import FeedbackForm from "./FeedbackForm";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const FeedbacksList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formDialog, setFormDialog] = useState({ open: false, feedback: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, feedback: null });

  const { data, isLoading, refetch } = useGetAdminFeedbacksQuery({
    page: page + 1,
    limit: rowsPerPage,
  });

  const [deleteFeedback, { isLoading: isDeleting }] = useDeleteFeedbackMutation();

  const feedbacks = data?.data || [];
  const pagination = data?.pagination || { total: 0, totalPages: 0 };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async () => {
    try {
      await deleteFeedback(deleteDialog.feedback._id).unwrap();
      toast.success("Feedback deleted successfully");
      setDeleteDialog({ open: false, feedback: null });
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete feedback");
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Student Feedbacks Management
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setFormDialog({ open: true, feedback: null })}
          >
            Add Feedback
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Feedback</TableCell>
              <TableCell align="center">Score Image</TableCell>
              {/* <TableCell align="center">Order</TableCell> */}
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : feedbacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No feedbacks found. Click "Add Feedback" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              feedbacks.map((feedback) => (
                <TableRow key={feedback._id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                        }}
                      >
                        {getInitials(feedback.studentName)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {feedback.studentName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 350,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {feedback.feedbackText}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {feedback.scoreImage ? (
                      <Chip
                        icon={<ImageIcon />}
                        label="Yes"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label="No"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  {/* <TableCell align="center">
                    <Chip
                      label={feedback.displayOrder}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell> */}
                  <TableCell align="center">
                    <Chip
                      label={feedback.isActive ? "Active" : "Inactive"}
                      size="small"
                      color={feedback.isActive ? "success" : "default"}
                      icon={
                        feedback.isActive ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          setFormDialog({ open: true, feedback })
                        }
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setDeleteDialog({ open: true, feedback })
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      <FeedbackForm
        open={formDialog.open}
        feedback={formDialog.feedback}
        onClose={() => setFormDialog({ open: false, feedback: null })}
        onSuccess={() => refetch()}
      />

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Feedback"
        message={`Are you sure you want to delete feedback from "${deleteDialog.feedback?.studentName}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog({ open: false, feedback: null })}
        confirmText="Delete"
        confirmColor="error"
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default FeedbacksList;
